import { formatMoney } from '@theme/money-formatting';
import { ThemeEvents } from '@theme/events';

const MIX_MATCH_BOGO = '[data-mnj-mix-match-bogo]';
const MIX_MATCH_DEAL = '[data-mnj-mix-match-deal]';
const GRID_DEAL = '[data-mnj-grid-deal]';
const CART_BOGO_PREVIEW = '[data-mnj-cart-bogo-preview]';
const ADDED_BAG_BOGO = '[data-mnj-added-bag-bogo]';
const MNJ_BOGO_GRID_UPDATED = 'mnj:bogo-grid-updated';

/** Per-unit amount paid for the first cart line (cents), after line-level discounts. */
function cartLineUnitCents(item) {
  if (!item || item.quantity < 1) return null;
  const q = item.quantity;
  if (typeof item.final_line_price === 'number') {
    return Math.floor(item.final_line_price / q);
  }
  if (typeof item.line_price === 'number') {
    return Math.floor(item.line_price / q);
  }
  if (typeof item.price === 'number') return item.price;
  return null;
}

/** Highest per-unit list price among cart lines (cents), or null if empty. */
function maxCartUnitCents(cart) {
  if (!cart?.items?.length) return null;
  let max = -1;
  for (const item of cart.items) {
    const u = cartLineUnitCents(item);
    if (u != null && u > max) max = u;
  }
  return max < 0 ? null : max;
}

/** BOGO 50% unit (cents): floor so e.g. $19.99 → $9.99, not $10.00. */
function bogoHalfUnitCents(base) {
  return Math.floor(base / 2);
}

function readMoneyConfig() {
  const el = document.getElementById('mnj-bogo-money-config');
  if (!el?.textContent?.trim()) return null;
  try {
    return JSON.parse(el.textContent);
  } catch {
    return null;
  }
}

/** Locale/market-aware cart JSON URL (hardcoded /cart.js breaks non-root locales). */
function getCartJsUrl() {
  try {
    const t = typeof globalThis !== 'undefined' ? globalThis.Theme : undefined;
    if (t?.routes?.cart_js_url) return t.routes.cart_js_url;
  } catch {
    /* ignore */
  }
  return '/cart.js';
}

/**
 * Nodes that show the “current” unit price; skips anything inside .price__hidden.
 * Updates every match so mobile/desktop duplicates both stay in sync.
 */
function findLivePriceTargets(container) {
  const selectors = [
    '.price__sale:not(.price__hidden) .price-item--sale.price',
    '.price__sale:not(.price__hidden) .price',
    '.price__regular:not(.price__hidden) .price',
  ];
  /** @type {Element[]} */
  const out = [];
  const seen = new Set();
  for (const sel of selectors) {
    container.querySelectorAll(sel).forEach((el) => {
      if (!(el instanceof Element) || seen.has(el)) return;
      if (el.closest('.price__hidden')) return;
      seen.add(el);
      out.push(el);
    });
  }
  if (out.length) return out;
  const fallback =
    container.querySelector('.price-item--sale.price') || container.querySelector('.price');
  return fallback ? [fallback] : [];
}

function setLivePriceText(container, cents, config) {
  if (!config?.moneyFormat || !config.currency) return;
  const withCur = container.getAttribute('data-mnj-bogo-with-currency') === 'true';
  const fmt = withCur ? config.moneyWithCurrencyFormat || config.moneyFormat : config.moneyFormat;
  const text = formatMoney(cents, fmt, config.currency);
  for (const target of findLivePriceTargets(container)) {
    target.textContent = text;
  }
}

function applyMixMatchBogo(cart, mixRows) {
  const qty = typeof cart.item_count === 'number' ? cart.item_count : 0;
  const one = qty === 1;
  const item = one && cart.items && cart.items[0] ? cart.items[0] : null;
  const cartUnit = item ? cartLineUnitCents(item) : null;

  mixRows.forEach((el) => {
    if (!one) {
      el.hidden = false;
      return;
    }
    const raw =
      el.getAttribute('data-mnj-bogo-base-cents') ?? el.getAttribute('data-mnj-bogo-pdp-cents');
    const cardCents = raw != null ? parseInt(raw, 10) : NaN;
    if (Number.isNaN(cardCents) || cartUnit == null) return;
    el.hidden = cardCents > cartUnit;
  });
}

/** True if this object looks like a Shopify /cart.js payload */
function isCartLike(resource) {
  return (
    resource != null &&
    typeof resource === 'object' &&
    (Array.isArray(resource.items) || typeof resource.item_count === 'number')
  );
}

function formatCents(cents, config, withCurrency) {
  if (!config?.moneyFormat || !config.currency) return '';
  const fmt = withCurrency ? config.moneyWithCurrencyFormat || config.moneyFormat : config.moneyFormat;
  return formatMoney(cents, fmt, config.currency);
}

/** Update deal card: BOGO badge + half price, or SALE badge + sale price + Save %. */
function applyDealCard(card, { showBogo, base, wasCents, config }) {
  if (!(card instanceof Element)) return;
  const half = bogoHalfUnitCents(base);
  const nowCents = showBogo ? half : base;
  const badgeBogo = card.querySelector('[data-mnj-deal-badge-bogo]');
  const badgeSale = card.querySelector('[data-mnj-deal-badge-sale]');
  const caption = card.querySelector('[data-mnj-bogo-caption]');
  const wasEl = card.querySelector('[data-mnj-bogo-was]');
  const nowEl = card.querySelector('[data-mnj-bogo-now]');
  const saveEl = card.querySelector('[data-mnj-bogo-save]');

  if (badgeBogo instanceof HTMLElement) badgeBogo.hidden = !showBogo;
  if (badgeSale instanceof HTMLElement) badgeSale.hidden = showBogo;
  if (caption instanceof HTMLElement) caption.hidden = !showBogo;

  if (wasEl instanceof HTMLElement) {
    if (wasCents > nowCents) {
      wasEl.textContent = formatCents(wasCents, config, false);
      wasEl.hidden = false;
    } else {
      wasEl.hidden = true;
    }
  }
  if (nowEl) nowEl.textContent = formatCents(nowCents, config, false);

  const pct =
    wasCents > nowCents ? Math.floor(((wasCents - nowCents) * 100) / wasCents) : 0;
  if (saveEl instanceof HTMLElement) {
    if (pct > 0) {
      saveEl.textContent = `Save ${pct}%`;
      saveEl.hidden = false;
    } else {
      saveEl.hidden = true;
    }
  }
}

function cartBogoQualifies(cart, base) {
  const totalQty =
    typeof cart.item_count === 'number'
      ? cart.item_count
      : Array.isArray(cart.items)
        ? cart.items.reduce((s, line) => s + (line.quantity || 0), 0)
        : 0;
  const cartMax = maxCartUnitCents(cart);
  const nextOrdinal = totalQty + 1;
  const isSecondOrEvenSlot = nextOrdinal % 2 === 0;
  const cartHasQualifyingUnit = cartMax != null && cartMax >= base;
  return isSecondOrEvenSlot && cartHasQualifyingUnit;
}

/** Added-to-bag recs: preview 2nd-item BOGO whenever the cart already has a qualifying pair item. */
function cartBogoQualifiesForAddedBag(cart, base) {
  const totalQty =
    typeof cart.item_count === 'number'
      ? cart.item_count
      : Array.isArray(cart.items)
        ? cart.items.reduce((s, line) => s + (line.quantity || 0), 0)
        : 0;
  if (totalQty < 1) return false;
  const cartMax = maxCartUnitCents(cart);
  return cartMax != null && cartMax >= base;
}

function readWasCents(wrap, base) {
  const compareRaw = wrap.getAttribute('data-mnj-bogo-compare-cents');
  const compareParsed = compareRaw != null ? parseInt(compareRaw, 10) : NaN;
  return !Number.isNaN(compareParsed) && compareParsed > base ? compareParsed : base;
}

/** “Often bought together” rows in the added-to-bag sheet. */
function applyAddedBagBogo(cart, bagNodes, config) {
  if (!config || bagNodes.length === 0) return;

  bagNodes.forEach((wrap) => {
    const baseRaw = wrap.getAttribute('data-mnj-bogo-base-cents');
    if (baseRaw == null) return;
    const base = parseInt(baseRaw, 10);
    if (Number.isNaN(base)) return;

    const card = wrap.querySelector('[data-mnj-bogo-bogo-row]') ?? wrap.querySelector('.mnj-bogo-deal');
    applyDealCard(card, {
      showBogo: cartBogoQualifiesForAddedBag(cart, base),
      base,
      wasCents: readWasCents(wrap, base),
      config,
    });
  });
}

/** PDP mix & match deal cards — same BOGO vs sale toggle as added-to-bag. */
function applyMixMatchDeal(cart, mixWraps, config) {
  if (!config || mixWraps.length === 0) return;

  mixWraps.forEach((wrap) => {
    const baseRaw = wrap.getAttribute('data-mnj-bogo-base-cents');
    if (baseRaw == null) return;
    const base = parseInt(baseRaw, 10);
    if (Number.isNaN(base)) return;

    const card = wrap.querySelector('[data-mnj-mix-match-deal-card]') ?? wrap.querySelector('.mnj-bogo-deal');
    applyDealCard(card, {
      showBogo: cartBogoQualifies(cart, base),
      base,
      wasCents: readWasCents(wrap, base),
      config,
    });
  });
}

/** Product grid / carousel cards — BOGO vs sale deal card. */
function applyGridDeal(cart, gridWraps, config) {
  if (!config || gridWraps.length === 0) return;

  gridWraps.forEach((wrap) => {
    const baseRaw = wrap.getAttribute('data-mnj-bogo-base-cents');
    if (baseRaw == null) return;
    const base = parseInt(baseRaw, 10);
    if (Number.isNaN(base)) return;

    const card = wrap.querySelector('[data-mnj-grid-deal-card]') ?? wrap.querySelector('.mnj-bogo-deal');
    applyDealCard(card, {
      showBogo: cartBogoQualifies(cart, base),
      base,
      wasCents: readWasCents(wrap, base),
      config,
    });
  });
}

function applyNextUnitBogo(cart, previewNodes, config) {
  if (!config || previewNodes.length === 0) return;
  const totalQty =
    typeof cart.item_count === 'number'
      ? cart.item_count
      : Array.isArray(cart.items)
        ? cart.items.reduce((s, line) => s + (line.quantity || 0), 0)
        : 0;
  const cartMax = maxCartUnitCents(cart);

  previewNodes.forEach((el) => {
    const baseRaw = el.getAttribute('data-mnj-bogo-base-cents');
    if (baseRaw == null) return;
    const base = parseInt(baseRaw, 10);
    if (Number.isNaN(base)) return;
    const nextOrdinal = totalQty + 1;
    const isSecondOrEvenSlot = nextOrdinal % 2 === 0;
    // Use >= so same-priced BOGO SKUs still preview the half-off “next” unit (strict > missed cartMax === base).
    const cartHasQualifyingUnit = cartMax != null && cartMax >= base;
    const displayCents =
      isSecondOrEvenSlot && cartHasQualifyingUnit ? bogoHalfUnitCents(base) : base;
    setLivePriceText(el, displayCents, config);
  });
}

/** Last cart payload from cart:update — used when rec grids morph after add-to-bag. */
let lastKnownCart = null;

/**
 * Apply BOGO UI from a cart object (no network). Used when cart:update already carries the cart.
 */
function applyBogoWithCart(cart) {
  if (!isCartLike(cart)) return;
  lastKnownCart = cart;
  const mixRows = document.querySelectorAll(MIX_MATCH_BOGO);
  const mixDeals = document.querySelectorAll(MIX_MATCH_DEAL);
  const gridDeals = document.querySelectorAll(GRID_DEAL);
  const previewNodes = document.querySelectorAll(CART_BOGO_PREVIEW);
  const bagNodes = document.querySelectorAll(ADDED_BAG_BOGO);
  if (
    mixRows.length === 0 &&
    mixDeals.length === 0 &&
    gridDeals.length === 0 &&
    previewNodes.length === 0 &&
    bagNodes.length === 0
  )
    return;
  const config = readMoneyConfig();
  applyMixMatchBogo(cart, mixRows);
  applyMixMatchDeal(cart, mixDeals, config);
  applyGridDeal(cart, gridDeals, config);
  applyNextUnitBogo(cart, previewNodes, config);
  applyAddedBagBogo(cart, bagNodes, config);
}

async function syncBogoCartPreview() {
  const mixRows = document.querySelectorAll(MIX_MATCH_BOGO);
  const mixDeals = document.querySelectorAll(MIX_MATCH_DEAL);
  const gridDeals = document.querySelectorAll(GRID_DEAL);
  const previewNodes = document.querySelectorAll(CART_BOGO_PREVIEW);
  const bagNodes = document.querySelectorAll(ADDED_BAG_BOGO);
  if (
    mixRows.length === 0 &&
    mixDeals.length === 0 &&
    gridDeals.length === 0 &&
    previewNodes.length === 0 &&
    bagNodes.length === 0
  )
    return;

  const config = readMoneyConfig();

  try {
    let res = /** @type {Response | null} */ (null);
    for (let attempt = 0; attempt < 4; attempt++) {
      res = await fetch(getCartJsUrl(), {
        headers: {
          Accept: 'application/json',
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
        credentials: 'same-origin',
        cache: 'no-store',
      });
      if (res.status === 429 || res.status === 503) {
        await new Promise((r) => setTimeout(r, Math.min(350 * 2 ** attempt, 8000)));
        continue;
      }
      break;
    }
    if (!res || !res.ok) return;
    const cart = await res.json();
    applyMixMatchBogo(cart, mixRows);
    applyMixMatchDeal(cart, mixDeals, config);
    applyGridDeal(cart, gridDeals, config);
    applyNextUnitBogo(cart, previewNodes, config);
    applyAddedBagBogo(cart, bagNodes, config);
  } catch {
    /* ignore */
  }
}

/**
 * Mobile: apply immediately from event payload (avoids race / cache / failed second fetch).
 */
function onCartUpdateEvent(event) {
  const resource = event?.detail?.resource;
  if (isCartLike(resource)) {
    applyBogoWithCart(resource);
    requestAnimationFrame(() => applyBogoWithCart(resource));
  }
  scheduleResync();
}

function onBogoGridUpdated(event) {
  const cart = event?.detail?.cart ?? lastKnownCart;
  if (isCartLike(cart)) {
    applyBogoWithCart(cart);
    requestAnimationFrame(() => applyBogoWithCart(cart));
  }
  scheduleResync();
}

/** Collapse burst events (cart:update + focus + touchend) into one /cart.js fetch. */
let resyncDebounceId = 0;
function scheduleResync() {
  if (resyncDebounceId) return;
  resyncDebounceId = window.setTimeout(() => {
    resyncDebounceId = 0;
    syncBogoCartPreview();
  }, 100);
}

/** Debounced window focus (mobile tab/app switches) to avoid spamming /cart.js */
let focusDebounceTimer = 0;
function scheduleResyncOnFocus() {
  if (focusDebounceTimer) clearTimeout(focusDebounceTimer);
  focusDebounceTimer = window.setTimeout(() => {
    focusDebounceTimer = 0;
    scheduleResync();
  }, 250);
}

/** After taps (e.g. closing cart drawer on phone), resync prices */
let touchDebounceTimer = 0;
function scheduleResyncOnTouchEnd() {
  if (touchDebounceTimer) clearTimeout(touchDebounceTimer);
  touchDebounceTimer = window.setTimeout(() => {
    touchDebounceTimer = 0;
    scheduleResync();
  }, 320);
}

function init() {
  scheduleResync();
  // Bubble on document + window so cart events are caught whether they stop at document or reach window.
  document.addEventListener('cart:update', onCartUpdateEvent);
  window.addEventListener('cart:update', onCartUpdateEvent);
  document.addEventListener(ThemeEvents.FilterUpdate, scheduleResync);
  document.addEventListener(MNJ_BOGO_GRID_UPDATED, onBogoGridUpdated);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') scheduleResync();
  });
  window.addEventListener('pageshow', () => scheduleResync());
  window.addEventListener('focus', scheduleResyncOnFocus);
  document.addEventListener(
    'touchend',
    (e) => {
      if (!(e.target instanceof Element)) return;
      if (e.target.closest('cart-drawer-component, .cart-drawer, dialog.cart-drawer, [data-cart-drawer]')) return;
      scheduleResyncOnTouchEnd();
    },
    { passive: true }
  );
  // Mobile: cart drawer + module load order can miss the first event; fewer delayed passes reduce /cart.js burst (theme dev 429).
  [600, 2200].forEach((ms) => setTimeout(syncBogoCartPreview, ms));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
